/* Sandboxed web worker for unsafe code execution */
import _ from "lodash";

import { MESSAGE_TYPE, IMessageEvent, WorkerMessage } from './SandboxWorkerTypes';
import { isDefined, IsNullOrEmpty } from "../Micro";
import { getGlobal } from '@Utils/Globals';
import { getGuid } from '@Utils/Utils';
import { EmeraldBaseUri } from '@Utils/Paths';
// ------------------------------------------------------------------------------------------------
var SandboxWorkers: { [key: string]: Sandbox } = {};
export interface SandBoxEval {
    id: string;
    timestamp: Date;
    code: string;
    input: { [name: string]: any };
    onError: (error: string, time: number) => void;
    onResults: (results: any, time: number) => void;
}
// ------------------------------------------------------------------------------------------------
export class Sandbox {
    // --------------------------------------------------------------------------------------------
    private worker: Worker;
    private isWorking: boolean;
    private isLoading: boolean;
    private pendingEval: { [id: string]: SandBoxEval }
    // --------------------------------------------------------------------------------------------
    constructor(name: string, extraLibs: string[] = null) {
        // Global save the sandbox
        SandboxWorkers[name] = this;
        this.pendingEval = {};
        this.isWorking = false;
        this.isLoading = true;

        // This is set only from external reference
        let ServerAddress = getGlobal("YodiBoardServerAddress");
        if (IsNullOrEmpty(ServerAddress)) {
            let sandboxPath = EmeraldBaseUri + "Content/SandboxWorker/App.js";

            // Create a new worker for this sandbox
            this.worker = new Worker(sandboxPath);
            this.worker.addEventListener("message", (event) => this.HandleExternalMessage(event));
            if (isDefined(extraLibs))
                this.worker.postMessage({
                    type: MESSAGE_TYPE.REQ_INIT,
                    scripts: extraLibs
                } as WorkerMessage);
        } else {
            this.worker = {
                postMessage: (data: WorkerMessage) => {
                    switch (data.type) {
                        // --------------------------------------
                        case MESSAGE_TYPE.REQ_EVAL: {
                            if (data.name != null)
                                console.group(data.name);

                            let result = null;
                            // Execute eval with try/catch
                            try {
                                let f = new Function("input", data.code);
                                result = f(data.input);

                                this.HandleExternalMessage({
                                    data: {
                                        type: MESSAGE_TYPE.RSP_RESULT,
                                        id: data.id,
                                        data: result
                                    }
                                } as any);
                            } catch (ex) {
                                this.HandleExternalMessage({
                                    data: {
                                        type: MESSAGE_TYPE.RSP_ERROR,
                                        id: data.id,
                                        error: ex.toString()
                                    }
                                } as any);
                            }

                            if (data.name != null)
                                console.groupEnd();
                        }
                            break;
                        // --------------------------------------
                        case MESSAGE_TYPE.REQ_STOP:
                            {
                                this.HandleExternalMessage({
                                    data: {
                                        type: MESSAGE_TYPE.RSP_STOPPED
                                    }
                                } as any);
                            }
                            break;
                        // --------------------------------------
                    }
                },
                terminate: () => { }
            } as any;
        }
    }
    // --------------------------------------------------------------------------------------------
    private HandleExternalMessage(event: IMessageEvent) {
        switch (event.data.type) {
            // --------------------------------------
            case MESSAGE_TYPE.RSP_ERROR:
                {
                    if (_.has(this.pendingEval, event.data.id)) {
                        this.pendingEval[event.data.id].onError(event.data.error, event.data.time);
                        delete this.pendingEval[event.data.id];
                    }
                }
                break;
            // --------------------------------------
            case MESSAGE_TYPE.RSP_RESULT:
                {
                    if (_.has(this.pendingEval, event.data.id)) {
                        this.pendingEval[event.data.id].onResults(event.data.data, event.data.time);
                        delete this.pendingEval[event.data.id];
                    }
                }
                break;
            // --------------------------------------
            case MESSAGE_TYPE.RSP_READY:
                this.isLoading = false;
                this.isWorking = true;
                break;
            // --------------------------------------
            case MESSAGE_TYPE.RSP_STOPPED:
                this.isWorking = false;
                break;
            // --------------------------------------
        }
    }
    // --------------------------------------------------------------------------------------------
    Eval(code: string,
        input: { [name: string]: any },
        onResults: (results: any) => void,
        onError?: (error: string) => void,
        argName: string = "input",
        name?: string // A name for messaging
    ): boolean {
        if (this.isWorking || this.isLoading) {
            let id = getGuid();

            // add the pending cmd
            this.pendingEval[id] = {
                id: id,
                code: code,
                input: input,
                onError: onError,
                onResults: onResults,
                timestamp: new Date()
            };

            // Send the request
            this.worker.postMessage({
                type: MESSAGE_TYPE.REQ_EVAL,
                id: id,
                argName: argName,
                code: code,
                input: input,
                name: name
            } as WorkerMessage);
            return true;
        }
        return false;
    }
    // --------------------------------------------------------------------------------------------
    EvalAsync<T = any>(code: string,
        input: { [name: string]: any },
        argName: string = "input",
        name?: string
    ): Promise<{ results: T, time: number }> {
        if (this.isWorking || this.isLoading) {
            return new Promise((resolve, rejects) => {
                let id = getGuid();

                // add the pending cmd
                this.pendingEval[id] = {
                    id: id,
                    code: code,
                    input: input,
                    onError: (error: string, time: number) => {
                        rejects({ error, time });
                    },
                    onResults: (results: any, time: number) => {
                        resolve({ results, time });
                    },
                    timestamp: new Date()
                };

                // Send the request
                this.worker.postMessage({
                    type: MESSAGE_TYPE.REQ_EVAL,
                    id: id,
                    argName: argName,
                    code: code,
                    input: input,
                    name: name
                } as WorkerMessage);
            });
        }
        return null;
    }
    // --------------------------------------------------------------------------------------------
    Stop() {
        this.worker.postMessage({
            type: MESSAGE_TYPE.REQ_STOP
        } as WorkerMessage);
    }
    // --------------------------------------------------------------------------------------------
    Terminate() {
        if (this.worker != null) {
            this.worker.terminate();
            this.isWorking = false;
            this.worker = null;
        }
    }
    // --------------------------------------------------------------------------------------------
}
// ------------------------------------------------------------------------------------------------
// Global variables
(window as any).SandboxWorkers = SandboxWorkers;